import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import Globe3D from "@/components/Globe3D";
import { useLang } from "@/i18n";
import { Activity, Globe2, Radio, Sparkles, Compass } from "lucide-react";

const COUNT_UP = (target, durationMs = 1400) => {
  // Animated counter from 0 → target on mount/change
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const from = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return value;
};

const StatTile = ({ icon: Icon, label, value, accent = "#00D4FF", testid, sub }) => {
  const display = COUNT_UP(value || 0);
  return (
    <div className="holo-panel relative overflow-hidden p-5 group" data-testid={testid}>
      <div
        className="absolute inset-x-0 -top-px h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
      />
      <div className="flex items-center justify-between">
        <div className="label-overline" style={{ color: accent }}>{label}</div>
        <Icon size={14} style={{ color: accent }} />
      </div>
      <div className="font-display font-black text-4xl tracking-tighter mt-3" style={{ color: accent }}>
        {display.toLocaleString()}
      </div>
      {sub && (
        <div className="font-mono-data text-[10px] tracking-[0.18em] uppercase text-white/45 mt-1">
          {sub}
        </div>
      )}
      <div
        className="absolute -right-12 -bottom-12 w-32 h-32 rounded-full blur-2xl opacity-30 group-hover:opacity-50 transition-opacity"
        style={{ background: accent }}
      />
    </div>
  );
};

export default function GlobalMap() {
  const { t, lang } = useLang();
  const [data, setData] = useState({
    points: [],
    stats: { total_transmissions: 0, active_missions: 0, countries: 0 },
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancel = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/missions/globe`)
      .then((r) => r.json())
      .then((d) => { if (!cancel) setData(d); })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);

  // Simulated live activity ticker (HUD)
  useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 1100);
    return () => clearInterval(id);
  }, []);

  // Activity feed (rolling simulated transmissions for ambience)
  const feed = useMemo(() => {
    const dests = ["TRAPPIST-1e", "Proxima b", "Mars", "Voyager 1", "Europa", "Titan", "Kepler-452b", "Luyten b", "Wolf 1061c", "Gliese 581g"];
    const sectors = ["AMS-2 KUIPER", "BR-1 LATAM", "EU-4 NORDIC", "AP-3 SAKURA", "NA-2 PACIFIC", "AF-1 SAHARA"];
    const seed = (tick * 7919) % 9973;
    const lines = [];
    for (let i = 0; i < 8; i++) {
      const s = (seed + i * 311) % 9973;
      const d = dests[s % dests.length];
      const sec = sectors[(s >> 3) % sectors.length];
      const t = (s % 60).toString().padStart(2, "0");
      lines.push({ id: i + tick * 10, dest: d, sector: sec, age: `${t}s`, hex: ((s * 16807) >>> 0).toString(16).slice(0, 6).toUpperCase() });
    }
    return lines;
  }, [tick]);

  return (
    <AppShell>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-overline flex items-center gap-2">
            <Globe2 size={12} /> {lang === "pt" ? "Operações Globais · Telemetria Anonimizada" : "Global Operations · Anonymized Telemetry"}
          </div>
          <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mt-2" data-testid="globe-title">
            {t("globe.title")}
          </h1>
          <div className="font-mono-data text-[11px] tracking-[0.18em] uppercase text-white/55 mt-2">
            {lang === "pt"
              ? "280 rotas ativas · 1550nm laser ISL · Amazon Kuiper relay"
              : "280 active routes · 1550nm laser ISL · Amazon Kuiper relay"}
          </div>
        </div>
        <div className="holo-panel px-4 py-2 flex items-center gap-3">
          <Radio size={14} className="text-[#62FFB0] blink" />
          <span className="font-mono-data text-[11px] tracking-[0.2em] uppercase text-[#62FFB0]">
            {lang === "pt" ? "TRANSMITINDO AO VIVO" : "LIVE TRANSMITTING"}
          </span>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-4 mb-4">
        <StatTile
          icon={Activity}
          label={t("globe.total")}
          value={data.stats.total_transmissions}
          accent="#00D4FF"
          sub={lang === "pt" ? "cápsulas transmitidas" : "capsules transmitted"}
          testid="globe-stat-total"
        />
        <StatTile
          icon={Radio}
          label={t("globe.active")}
          value={data.stats.active_missions}
          accent="#62FFB0"
          sub={lang === "pt" ? "rotas ativas em tempo real" : "live routes in flight"}
          testid="globe-stat-active"
        />
        <StatTile
          icon={Globe2}
          label={t("globe.countries")}
          value={data.stats.countries}
          accent="#A077FF"
          sub={lang === "pt" ? "nações de origem" : "origin nations"}
          testid="globe-stat-countries"
        />
        <StatTile
          icon={Sparkles}
          label={lang === "pt" ? "Distância Cumulativa" : "Cumulative Distance"}
          value={Math.round(data.stats.total_transmissions * 4.24 * 9.461)}
          accent="#FFD27A"
          sub={lang === "pt" ? "×10¹² km percorridos" : "×10¹² km traveled"}
          testid="globe-stat-distance"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* The globe with hundreds of arcs */}
        <div className="lg:col-span-2 holo-panel relative overflow-hidden scan-line h-[78vh] sm:h-[560px] lg:h-[620px]" data-testid="globe-canvas">
          <Globe3D points={data.points} routeCount={300} className="absolute inset-0" />

          {/* HUD overlay corners */}
          <div className="absolute top-3 left-3 holo-panel px-3 py-2 pointer-events-none">
            <div className="label-overline flex items-center gap-2">
              <Compass size={10} /> {lang === "pt" ? "Centro de Comando" : "Command Center"}
            </div>
            <div className="font-mono-data text-[10px] text-[#00D4FF] mt-0.5">
              LAT 0.00° · LON 0.00° · ORBIT GEO
            </div>
          </div>
          <div className="absolute top-3 right-3 holo-panel px-3 py-2 pointer-events-none text-right">
            <div className="label-overline">{lang === "pt" ? "Sinal Médio" : "Mean Signal"}</div>
            <div className="font-mono-data text-[10px] text-[#62FFB0] mt-0.5">-128.4 dBm</div>
          </div>
          <div className="absolute bottom-3 left-3 right-3 holo-panel px-3 py-2 pointer-events-none">
            <div className="flex items-center justify-between">
              <div className="font-mono-data text-[10px] tracking-[0.16em] uppercase text-white/60">
                {lang === "pt" ? "Sincronizando rotas" : "Synchronizing routes"}
              </div>
              <div className="font-mono-data text-[10px] text-[#00D4FF]">{(tick % 60).toString().padStart(2, "0")} / 60</div>
            </div>
            <div className="mt-1.5 h-[2px] bg-white/5 relative overflow-hidden">
              <div
                className="absolute top-0 h-full w-1/3"
                style={{
                  background: "linear-gradient(90deg, transparent, #00D4FF, transparent)",
                  animation: "scanX 2.6s linear infinite",
                }}
              />
            </div>
          </div>
        </div>

        {/* Live activity feed */}
        <div className="holo-panel p-5 flex flex-col h-[480px] sm:h-[520px] lg:h-[620px]" data-testid="globe-feed">
          <div className="label-overline mb-3 flex items-center gap-2">
            <Activity size={12} /> {lang === "pt" ? "Feed Ao Vivo" : "Live Feed"}
          </div>
          <div className="space-y-2 overflow-auto pr-1 flex-1">
            {feed.map((row) => (
              <div key={row.id} className="border border-white/5 p-2.5 hover:border-[#00D4FF]/40 transition-colors group"
                   style={{ animation: "feedIn .6s ease-out both" }}
                   data-testid={`feed-row-${row.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-mono-data text-[10px] tracking-[0.16em] uppercase text-[#00D4FF]">
                    TX-{row.hex}
                  </div>
                  <div className="font-mono-data text-[10px] text-white/45">{row.age}</div>
                </div>
                <div className="font-display font-bold text-sm mt-1 tracking-tight group-hover:text-[#62FFB0] transition-colors">
                  → {row.dest}
                </div>
                <div className="font-mono-data text-[10px] tracking-[0.16em] uppercase text-white/45 mt-1">
                  {row.sector}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Inline keyframes for HUD scan + feed entrance */}
      <style>{`
        @keyframes scanX {
          0% { left: -33%; }
          100% { left: 100%; }
        }
        @keyframes feedIn {
          0% { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </AppShell>
  );
}
